"use client";

import { createContext, Fragment, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { RecommendationFeedbackMutation, RecommendationChoiceContext } from "@batch-and-bowl/validators";
import { authClient } from "../auth-client";
import { changeRecommendationFeedback, getRecommendationSuppressions, type RecommendationSuppression } from "../api/recommendation-feedback";
import { extractErrorStatus } from "../errors";
import { GROCERY_LISTS_QUERY_KEY } from "../hooks/query-keys";

interface FeedbackContext {
  actorUserId: string;
  isCurrentActor: () => boolean;
  suppressions: RecommendationSuppression[];
  isLoading: boolean;
  isError: boolean;
  busy: boolean;
  reload: () => void;
  perform: (input: RecommendationFeedbackMutation, context?: RecommendationChoiceContext) => Promise<void>;
  message: string | null;
  retry: (() => Promise<void>) | null;
  undo: (() => Promise<void>) | null;
  registerStatus: () => () => void;
}
const Context = createContext<FeedbackContext | null>(null);
export const useRecommendationFeedback = () => useContext(Context);
type Operation = { actorUserId: string; input: RecommendationFeedbackMutation; context: RecommendationChoiceContext };

/** Application-owned controls; the API persists receipts without analytics transport in the UI. */
export function RecommendationFeedbackProvider({ children }: { children: ReactNode }) {
  const { data: session, isPending } = authClient.useSession();
  const actorUserId = isPending ? null : session?.user?.id ?? null;
  const actorRef = useRef(actorUserId);
  actorRef.current = actorUserId;
  const client = useQueryClient();
  const [pending, setPending] = useState(false);
  const [uncertain, setUncertain] = useState<Operation | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Operation | null>(null);
  const [embeddedCount, setEmbeddedCount] = useState(0);
  const registerStatus = useCallback(() => {
    setEmbeddedCount(count => count + 1);
    return () => setEmbeddedCount(count => count - 1);
  }, []);
  const operationRef = useRef<Operation | null>(null);
  const query = useQuery({
    queryKey: ["recommendation-feedback", actorUserId],
    queryFn: ({ signal }) => getRecommendationSuppressions(actorUserId!, signal),
    enabled: Boolean(actorUserId),
  });

  useEffect(() => {
    operationRef.current = null;
    setPending(false);
    setUncertain(null);
    setMessage(null);
    setLastSaved(null);
  }, [actorUserId]);

  const execute = async (operation: Operation) => {
    if (actorRef.current !== operation.actorUserId) throw new Error("Signed-in account changed");
    operationRef.current = operation;
    setPending(true);
    setUncertain(null);
    try {
      await client.cancelQueries({ queryKey: ["recommendation-feedback", operation.actorUserId], exact: true });
      if (actorRef.current !== operation.actorUserId || operationRef.current !== operation) throw new Error("Signed-in account changed");
      const result = await changeRecommendationFeedback(operation.actorUserId, operation.input, operation.context);
      // A read may also have started while the write was pending.
      await client.cancelQueries({ queryKey: ["recommendation-feedback", operation.actorUserId], exact: true });
      if (actorRef.current !== operation.actorUserId || operationRef.current !== operation) throw new Error("Signed-in account changed");
      client.setQueryData(["recommendation-feedback", operation.actorUserId], result.suppressions);
      // Recommendation reads change; accepted plans and groceries do not.
      void client.invalidateQueries({ queryKey: ["swap-suggestions"] });
      void client.invalidateQueries({ predicate: query => query.queryKey[0] === GROCERY_LISTS_QUERY_KEY && query.queryKey.includes("savings-suggestions") });
      setMessage(operation.input.action === "suppress" ? "Saved for your suggestions." : "Recommendation choices restored.");
      setLastSaved(operation.input.action === "suppress" ? operation : null);
      operationRef.current = null;
    } catch (error) {
      if (actorRef.current !== operation.actorUserId || operationRef.current !== operation) throw error;
      const status = extractErrorStatus(error);
      if (status === 0 || status === 408 || status === 429 || status >= 500) {
        setUncertain(operation);
      } else {
        operationRef.current = null;
        void query.refetch();
        setLastSaved(null);
        setMessage(status === 409 ? "These choices changed. Please check them and try again." : "Couldn't save your recommendation choice. Please try again.");
      }
      throw error;
    } finally {
      if (actorRef.current === operation.actorUserId && (!operationRef.current || operationRef.current === operation)) setPending(false);
    }
  };
  const perform = async (input: RecommendationFeedbackMutation, context: RecommendationChoiceContext = { surface: "profile" }) => {
    if (!actorUserId || actorRef.current !== actorUserId) throw new Error("Signed-in account changed");
    if (operationRef.current) throw new Error("Confirm the pending feedback first");
    await execute({ actorUserId, input, context });
  };
  const undo = lastSaved?.actorUserId === actorUserId && lastSaved.input.action === "suppress" ? async () => {
    const saved = lastSaved.input;
    if (saved.action !== "suppress" || operationRef.current || actorRef.current !== lastSaved.actorUserId) return;
    await execute({ actorUserId: lastSaved.actorUserId, context: lastSaved.context, input: { action: "restore", operationId: crypto.randomUUID(), recipeId: saved.recipeId, scope: saved.scope, expectedRevisionId: saved.operationId } });
  } : null;
  return <Context.Provider value={actorUserId ? {
    isCurrentActor: () => actorRef.current === actorUserId,
    actorUserId, suppressions: query.data ?? [], isLoading: query.isLoading, isError: query.isError,
    busy: pending || Boolean(uncertain), reload: () => { void query.refetch(); }, perform,
    message: uncertain ? "We couldn't confirm your recommendation choice. Retry safely before making another change." : message,
    retry: uncertain && !pending ? () => execute(uncertain) : null,
    undo: !pending && !uncertain ? undo : null,
    registerStatus,
  } : null}>
    <Fragment key={actorUserId ?? "signed-out"}>{children}</Fragment>
    {embeddedCount === 0 && <div className="fixed inset-x-4 bottom-24 z-[100] mx-auto max-w-lg"><RecommendationFeedbackStatus embedded={false} /></div>}
  </Context.Provider>;
}

/** Render inside modal focus/interaction boundaries; a global fallback covers closed flows. */
export function RecommendationFeedbackStatus({ embedded = true }: { embedded?: boolean }) {
  const feedback = useRecommendationFeedback();
  const register = feedback?.registerStatus;
  useEffect(() => embedded ? register?.() : undefined, [embedded, register]);
  if (!feedback?.message) return null;
  return <div role="status" className="rounded-xl border border-border bg-card p-3 text-sm shadow-sm">
    <p>{feedback.message}</p>
    {feedback.retry && <button type="button" className="tap-target font-semibold underline" onClick={() => void feedback.retry!().catch(() => {})}>Retry feedback</button>}
    {feedback.undo && <button type="button" className="tap-target font-semibold underline" onClick={() => void feedback.undo!().catch(() => {})}>Undo</button>}
  </div>;
}
